import type { Note } from '../domain/note'
import { normalizeSettingsRecord, type SettingsRecord } from '../domain/settings'
import type { SupabaseClient } from '@supabase/supabase-js'
import { getSupabaseClient } from '../supabase/client'
import { getSupabaseConfig } from '../supabase/config'
import { APP_SETTINGS } from '../config/appSettings'
import { parsePlanLimitError } from '../auth/planLimits'

export interface SyncReceipt {
  note: Note
  serverReceivedAt: number
}

export interface SettingsSyncReceipt {
  settings: SettingsRecord
  serverReceivedAt: number
}

export class SettingsSyncUnavailableError extends Error {
  constructor() {
    super('Settings are saved locally, but this server has not installed settings synchronization yet.')
    this.name = 'SettingsSyncUnavailableError'
  }
}

export class LegacyImageClientRevisionError extends Error {
  constructor() {
    super('A stale client tried to remove an image note. The server copy was preserved.')
    this.name = 'LegacyImageClientRevisionError'
  }
}

export class NoteLimitExceededError extends Error {
  constructor(
    readonly accountType: string,
    readonly limit: number,
    readonly usage: number,
  ) {
    super(`Your ${accountType} account has reached its ${limit.toLocaleString()}-note limit. This note remains saved on this device.`)
    this.name = 'NoteLimitExceededError'
  }
}

export interface SyncTransport {
  readonly id: string
  pushNote(note: Note, idempotencyKey: string, clientSchemaVersion: number): Promise<SyncReceipt>
  getNote(noteId: string): Promise<Note | null>
  pullNotes(cursor: string | null): Promise<{ notes: Note[]; cursor: string }>
  pushSettings(settings: SettingsRecord, idempotencyKey: string): Promise<SettingsSyncReceipt>
  pullSettings(): Promise<SettingsRecord[]>
  getInventory?(): Promise<RemoteSyncInventory>
}

export interface RemoteSyncInventory {
  noteIds: string[]
  assetIds: string[]
  missingAssetObjectIds: string[]
}

interface PullCursor {
  after: string
  noteId: string
}

interface SyncRow {
  note: Note
  server_received_at: string
}

interface PullRow {
  note: Note
  server_updated_at: string
  note_id: string
  link_previews?: unknown
}

interface GetNoteRow {
  note: Note
  server_updated_at: string
  link_previews?: unknown
}

interface SettingsSyncRow {
  setting: unknown
  server_received_at: string
}

interface SettingsPullRow {
  setting: unknown
}

interface InventoryRow {
  note_ids: string[]
  asset_ids: string[]
  missing_asset_object_ids: string[]
}

const INITIAL_CURSOR: PullCursor = {
  after: '1970-01-01T00:00:00.000Z',
  noteId: '00000000-0000-0000-0000-000000000000',
}

function decodeCursor(cursor: string | null): PullCursor {
  if (!cursor) return INITIAL_CURSOR
  try {
    const value = JSON.parse(cursor) as Partial<PullCursor>
    if (typeof value.after === 'string' && typeof value.noteId === 'string') {
      return { after: value.after, noteId: value.noteId }
    }
  } catch {
    // Start a complete pull if local cursor metadata was corrupted.
  }
  return INITIAL_CURSOR
}

function encodeCursor(cursor: PullCursor): string {
  return JSON.stringify(cursor)
}

function errorCode(error: { code?: string; message: string }): string {
  return error.code ? `${error.code}: ${error.message}` : error.message
}

function noteLimitError(error: { message: string; details?: string }): NoteLimitExceededError | null {
  if (error.message !== 'NOTE_LIMIT_REACHED') return null
  try {
    const details = JSON.parse(error.details ?? '') as Record<string, unknown>
    if (
      typeof details.accountType === 'string'
      && typeof details.limit === 'number'
      && Number.isSafeInteger(details.limit)
      && typeof details.usage === 'number'
      && Number.isSafeInteger(details.usage)
    ) {
      return new NoteLimitExceededError(details.accountType, details.limit, details.usage)
    }
  } catch {
    // The stable server code still identifies the policy failure if details
    // were stripped by an older PostgREST deployment.
  }
  return new NoteLimitExceededError('current', 0, 0)
}

function throwSettingsError(error: { code?: string; message: string }): never {
  if (error.code === 'PGRST202') throw new SettingsSyncUnavailableError()
  throw new Error(errorCode(error))
}

export class SupabaseSyncTransport implements SyncTransport {
  readonly id = 'supabase'

  constructor(
    private readonly isDev: boolean,
    private readonly loadClient: () => Promise<SupabaseClient> = getSupabaseClient,
  ) {}

  async pushNote(
    note: Note,
    idempotencyKey: string,
    clientSchemaVersion: number,
  ): Promise<SyncReceipt> {
    const client = await this.loadClient()
    const { data, error } = await client.rpc('sync_note', {
      p_is_dev: this.isDev,
      p_idempotency_key: idempotencyKey,
      p_note_id: note.id,
      p_payload: note,
      p_modified_at: note.modifiedAt,
      p_last_modified_device_id: note.lastModifiedDeviceId,
      p_revision_id: note.revisionId,
      p_client_schema_version: clientSchemaVersion,
    })
    if (
      error?.code === '0A000'
      && error.message.includes('Update miniMany before deleting an image note')
    ) {
      throw new LegacyImageClientRevisionError()
    }
    if (error) {
      const limitError = noteLimitError(error)
      if (limitError) throw limitError
      // A restore from Trash that would put the account over its image limit (the app checks first; this is the backstop).
      const planError = parsePlanLimitError(error)
      if (planError) throw planError
    }
    if (error) throw new Error(errorCode(error))
    const row = (data as SyncRow[] | null)?.[0]
    if (!row?.note) throw new Error('Supabase returned no synchronized note.')
    return {
      note: { ...row.note, localSyncState: 'synced' },
      serverReceivedAt: Date.parse(row.server_received_at),
    }
  }

  async pullNotes(cursor: string | null): Promise<{ notes: Note[]; cursor: string }> {
    const current = decodeCursor(cursor)
    const client = await this.loadClient()
    const { data, error } = await client.rpc('pull_notes', {
      p_is_dev: this.isDev,
      p_after: current.after,
      p_after_note_id: current.noteId,
      p_limit: APP_SETTINGS.sync.pullPageSize,
    })
    if (error) throw new Error(errorCode(error))
    const rows = (data ?? []) as PullRow[]
    const last = rows.at(-1)
    return {
      notes: rows.map((row) => ({
        ...row.note,
        linkPreviews: Array.isArray(row.link_previews) ? row.link_previews : [],
        localSyncState: 'synced',
      }) as Note),
      cursor: last
        ? encodeCursor({ after: last.server_updated_at, noteId: last.note_id })
        : encodeCursor(current),
    }
  }

  async getNote(noteId: string): Promise<Note | null> {
    const client = await this.loadClient()
    const { data, error } = await client.rpc('get_note', {
      p_is_dev: this.isDev,
      p_note_id: noteId,
    })
    if (error) throw new Error(errorCode(error))
    const row = (data as GetNoteRow[] | null)?.[0]
    return row?.note
      ? {
          ...row.note,
          linkPreviews: Array.isArray(row.link_previews) ? row.link_previews : [],
          localSyncState: 'synced',
        } as Note
      : null
  }

  async pushSettings(settings: SettingsRecord, idempotencyKey: string): Promise<SettingsSyncReceipt> {
    const client = await this.loadClient()
    const { data, error } = await client.rpc('sync_setting', {
      p_is_dev: this.isDev,
      p_idempotency_key: idempotencyKey,
      p_setting_type: settings.recordType,
      p_setting_id: settings.recordType === 'global' ? 'global' : settings.deviceId,
      p_payload: settings,
      p_modified_at: settings.modifiedAt,
      p_last_modified_device_id: settings.lastModifiedDeviceId,
      p_revision_id: settings.revisionId,
    })
    if (error) throwSettingsError(error)
    const row = (data as SettingsSyncRow[] | null)?.[0]
    const normalized = normalizeSettingsRecord(row?.setting)
    if (!row || !normalized) throw new Error('Supabase returned invalid synchronized settings.')
    return { settings: normalized, serverReceivedAt: Date.parse(row.server_received_at) }
  }

  async pullSettings(): Promise<SettingsRecord[]> {
    const client = await this.loadClient()
    const { data, error } = await client.rpc('pull_settings', { p_is_dev: this.isDev })
    if (error) throwSettingsError(error)
    return ((data ?? []) as SettingsPullRow[])
      .map((row) => normalizeSettingsRecord(row.setting))
      .filter((settings): settings is SettingsRecord => settings !== null)
  }

  async getInventory(): Promise<RemoteSyncInventory> {
    const client = await this.loadClient()
    const { data, error } = await client.rpc('get_sync_inventory', { p_is_dev: this.isDev })
    if (error) throw new Error(errorCode(error))
    const row = (data as InventoryRow[] | null)?.[0]
    if (
      !row
      || !Array.isArray(row.note_ids)
      || !Array.isArray(row.asset_ids)
      || !Array.isArray(row.missing_asset_object_ids)
    ) throw new Error('Supabase returned an invalid restore inventory.')
    return {
      noteIds: row.note_ids,
      assetIds: row.asset_ids,
      missingAssetObjectIds: row.missing_asset_object_ids,
    }
  }
}

export function createSyncTransport(): SyncTransport | null {
  const config = getSupabaseConfig()
  return config ? new SupabaseSyncTransport(config.isDev) : null
}

export class HttpSyncTransport implements SyncTransport {
  readonly id = 'http'

  constructor(private readonly apiBaseUrl: string) {}

  async pushNote(
    note: Note,
    idempotencyKey: string,
    _clientSchemaVersion: number,
  ): Promise<SyncReceipt> {
    const response = await fetch(`${this.apiBaseUrl}/sync/notes/${note.id}`, {
      method: 'PUT',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        'Idempotency-Key': idempotencyKey,
      },
      body: JSON.stringify(note),
    })
    if (!response.ok) throw new Error(`Sync push failed with ${response.status}.`)
    return response.json() as Promise<SyncReceipt>
  }

  async pullNotes(cursor: string | null): Promise<{ notes: Note[]; cursor: string }> {
    const query = cursor ? `?cursor=${encodeURIComponent(cursor)}` : ''
    const response = await fetch(`${this.apiBaseUrl}/sync/notes${query}`, {
      credentials: 'include',
    })
    if (!response.ok) throw new Error(`Sync pull failed with ${response.status}.`)
    return response.json() as Promise<{ notes: Note[]; cursor: string }>
  }

  async getNote(noteId: string): Promise<Note | null> {
    const response = await fetch(`${this.apiBaseUrl}/sync/notes/${encodeURIComponent(noteId)}`, {
      credentials: 'include',
    })
    if (response.status === 404) return null
    if (!response.ok) throw new Error(`Note retrieval failed with ${response.status}.`)
    return response.json() as Promise<Note>
  }

  async pushSettings(settings: SettingsRecord, idempotencyKey: string): Promise<SettingsSyncReceipt> {
    const response = await fetch(`${this.apiBaseUrl}/sync/settings/${encodeURIComponent(settings.key)}`, {
      method: 'PUT',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        'Idempotency-Key': idempotencyKey,
      },
      body: JSON.stringify(settings),
    })
    if (!response.ok) throw new Error(`Settings sync push failed with ${response.status}.`)
    return response.json() as Promise<SettingsSyncReceipt>
  }

  async pullSettings(): Promise<SettingsRecord[]> {
    const response = await fetch(`${this.apiBaseUrl}/sync/settings`, { credentials: 'include' })
    if (!response.ok) throw new Error(`Settings sync pull failed with ${response.status}.`)
    return response.json() as Promise<SettingsRecord[]>
  }
}
